// ============================================================
// AppDialog - the in-app confirm / prompt / choice dialog that stands
// in for window.confirm and window.prompt. `onClose` receives the
// answer: true, the typed text, the chosen value, or null for Cancel.
// ============================================================
import { useState } from 'react';
import common from '../styles/common.module.css';
import useFocusTrap from '../hooks/useFocusTrap.js';
import useModalBackdrop from '../hooks/useModalBackdrop';

const LABELS = new Map([
  ['prompt', 'Input dialog'],
  ['choice', 'Choice dialog'],
]);

/** One button per choice, then Cancel, stacked. */
function ChoiceButtons({ choices, onClose }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {choices.map((c) => (
        <button
          key={c.value}
          className={common.btnPrimary}
          style={{ width: '100%', textAlign: 'left', padding: '8px 12px' }}
          onClick={() => onClose(c.value)}
        >
          {c.label}
        </button>
      ))}
      <button className={common.btnSecondary} style={{ width: '100%', marginTop: 2 }} onClick={() => onClose(null)}>
        Cancel
      </button>
    </div>
  );
}

/** Cancel, and OK (a prompt) or Confirm (a confirm). */
function AnswerButtons({ type, inputVal, onClose }) {
  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
      <button className={common.btnSecondary} onClick={() => onClose(null)}>
        Cancel
      </button>
      {type === 'prompt' && (
        <button className={common.btnPrimary} onClick={() => onClose(inputVal)}>
          OK
        </button>
      )}
      {type === 'confirm' && (
        <button className={common.btnPrimary} onClick={() => onClose(true)}>
          Confirm
        </button>
      )}
    </div>
  );
}

/** A prompt's text box: Enter answers with the text, Escape cancels. */
function PromptInput({ value, onChange, onClose }) {
  return (
    <input
      className={common.input}
      style={{ width: '100%', boxSizing: 'border-box', marginBottom: 12 }}
      // eslint-disable-next-line jsx-a11y/no-autofocus -- the prompt dialog opened for this one answer
      autoFocus
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onClose(value);
        if (e.key === 'Escape') onClose(null);
      }}
    />
  );
}

export default function AppDialog({ dialog, onClose }) {
  const [inputVal, setInputVal] = useState(dialog?.defaultValue || '');
  const trapRef = useFocusTrap(!!dialog);
  const backdrop = useModalBackdrop(() => onClose(null), trapRef);
  if (!dialog) return null;
  return (
    <div className={common.modal} onClick={backdrop.onBackdropClick} role="presentation">
      <div
        className={common.modalContent}
        style={{ maxWidth: 360 }}
        ref={backdrop.boxRef}
        role="dialog"
        aria-modal="true"
        aria-label={LABELS.get(dialog.type) ?? 'Confirmation dialog'}
      >
        <div style={{ color: 'var(--text-primary)', marginBottom: 12, fontSize: 13 }}>{dialog.message}</div>
        {dialog.type === 'prompt' && <PromptInput value={inputVal} onChange={setInputVal} onClose={onClose} />}
        {dialog.type === 'choice' ? (
          <ChoiceButtons choices={dialog.choices} onClose={onClose} />
        ) : (
          <AnswerButtons type={dialog.type} inputVal={inputVal} onClose={onClose} />
        )}
      </div>
    </div>
  );
}
